import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Avatar,
  Button,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { 
  AccountCircle,
  TrendingUp,
  EmojiEvents,
  Logout,
  MoreVert,
  Edit,
  Verified,
  Phone,
  Email,
  AccountBalance,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const DashboardPage = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <Box sx={{ pt: 12, pb: 8, minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <AccountCircle sx={{ fontSize: 60 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Welcome back, {userProfile?.firstName || 'Trader'}!
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  {userProfile?.email}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  {userProfile?.isVerified && (
                    <Chip 
                      icon={<Verified />} 
                      label="Verified" 
                      sx={{ backgroundColor: 'rgba(76, 175, 80, 0.2)', color: 'white' }}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item>
                <IconButton 
                  color="inherit"
                  onClick={handleMenuOpen}
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => {
                    handleMenuClose();
                    navigate('/edit-profile');
                  }}>
                    <Edit sx={{ mr: 1 }} /> Edit Profile
                  </MenuItem>
                  <MenuItem onClick={() => setLogoutDialogOpen(true)}>
                    <Logout sx={{ mr: 1 }} /> Logout
                  </MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        <Grid container spacing={4}>
          {/* Account Information */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Account Information
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <AccountCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Full Name"
                        secondary={`${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Email color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Email"
                        secondary={userProfile?.email}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Phone color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Mobile Number"
                        secondary={userProfile?.mobileNumber || 'Not provided'}
                      />
                    </ListItem>
                    {userProfile?.xmAccountNumber && (
                      <ListItem>
                        <ListItemIcon>
                          <AccountBalance color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="XM Account"
                          secondary={userProfile.xmAccountNumber}
                        />
                      </ListItem>
                    )}
                    {userProfile?.weltradeAccountNumber && (
                      <ListItem>
                        <ListItemIcon>
                          <TrendingUp color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Weltrade Account"
                          secondary={userProfile.weltradeAccountNumber}
                        />
                      </ListItem>
                    )}
                  </List>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>
                    Trading Preferences
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {userProfile?.tradingFinancials && (
                      <Chip label="Financials" color="primary" variant="outlined" />
                    )}
                    {userProfile?.tradingSynthetics && (
                      <Chip label="Synthetics" color="secondary" variant="outlined" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Quick Actions
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Button 
                  variant="outlined" 
                  startIcon={<EmojiEvents />}
                  onClick={() => navigate('/leaderboard')}
                >
                  Leaderboard
                </Button>
              </Grid>
              <Grid item>
                <Button 
                  variant="outlined" 
                  startIcon={<Star />}
                  onClick={() => navigate('/prizes')}
                >
                  View Prizes
                </Button>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      </Container>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLogout} color="error" variant="contained">
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;